import { useContext, useEffect, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { useAPI } from '../services/api';
import ToastContext from '../store/toast-context';

// <input type="date"> works in local YYYY-MM-DD, while the API stores full timestamps
const toInputValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const fromInputValue = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toISOString();
};

const TaskScheduleRow = ({ task, onSave }) => {
  const [openDate, setOpenDate] = useState(toInputValue(task.open_date));
  const [lockDate, setLockDate] = useState(toInputValue(task.lock_date));
  const [saving, setSaving] = useState(false);

  const changed =
    openDate !== toInputValue(task.open_date) || lockDate !== toInputValue(task.lock_date);
  const invalid = openDate && lockDate && openDate >= lockDate;

  const handleSave = async () => {
    setSaving(true);
    await onSave(task.id, {
      open_date: fromInputValue(openDate),
      lock_date: fromInputValue(lockDate),
    });
    setSaving(false);
  };

  return (
    <tr>
      <td>{task.name}</td>
      <td>{task.is_open ? 'Open' : 'Locked'}</td>
      <td>
        <Form.Control
          type="date"
          aria-label={`${task.name} opens on`}
          value={openDate}
          onChange={(e) => setOpenDate(e.target.value)}
        />
      </td>
      <td>
        <Form.Control
          type="date"
          aria-label={`${task.name} locks on`}
          value={lockDate}
          isInvalid={invalid}
          onChange={(e) => setLockDate(e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Must be after the open date</Form.Control.Feedback>
      </td>
      <td>
        <Button className="dark-btn" disabled={!changed || invalid || saving} onClick={handleSave}>
          Save
        </Button>
      </td>
    </tr>
  );
};

const TaskSchedulePage = ({ title = 'File Schedule' }) => {
  const api = useAPI();
  const { error, success } = useContext(ToastContext);
  const [tasks, setTasks] = useState([]);

  const loadTasks = async () => {
    const { data } = await api.get('/task');
    setTasks(data);
  };

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const saveTask = async (id, dates) => {
    try {
      await api.put(`/task/${id}`, dates);
      await loadTasks();
      success('Schedule saved');
    } catch (e) {
      error(e.response?.data ?? e.message);
    }
  };

  return (
    <>
      <h1 className="display-4 fw-normal mb-3">{title}</h1>
      <p>
        Each file is available from its open date until 12:00 AM on its lock date. To keep a file
        open through November 30, set it to lock on December 1. Files with both dates set re-open on
        their own every year, and a blank date means no limit.
      </p>
      <Table responsive>
        <thead>
          <tr>
            <th>File</th>
            <th>Status</th>
            <th>Opens On</th>
            <th>Locks On</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <TaskScheduleRow
              // Re-mount on reload so the inputs reset to the saved dates
              key={`${task.id}-${task.open_date}-${task.lock_date}`}
              task={task}
              onSave={saveTask}
            />
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default TaskSchedulePage;
